import { useEffect, useMemo, useRef, useState } from "react";
import { SelectItemDto } from "../../types/SelectItemDto";
import "./AppSearchSelect.css";
import { useTranslation } from "react-i18next";

interface AppSearchSelectProps {
    options: SelectItemDto[];
    value?: SelectItemDto | SelectItemDto[] | null;
    onChange?: (value: SelectItemDto | SelectItemDto[] | null) => void;
    isMulti?: boolean;
    disabled?: boolean;
    placeholder?: string;
    noOptionsText?: string;
    isInvalid?: boolean;
}

const normalizeToArray = (value: SelectItemDto | SelectItemDto[] | null | undefined): SelectItemDto[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

export default function AppSearchSelect({
    options,
    value,
    onChange,
    isMulti = false,
    disabled = false,
    placeholder = "Selecciona...",
    noOptionsText = "Sin resultados",
    isInvalid = false,
}: AppSearchSelectProps) {
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [internalValue, setInternalValue] = useState<SelectItemDto | SelectItemDto[] | null>(isMulti ? [] : null);

    const selectedValue = value !== undefined ? value : internalValue;
    const selectedItems = normalizeToArray(selectedValue);

    const filteredOptions = useMemo(() => {
        const search = searchText.trim().toLowerCase();
        if (!search) return options;
        return options.filter((option) => option.name.toLowerCase().includes(search));
    }, [options, searchText]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const emitChange = (nextValue: SelectItemDto | SelectItemDto[] | null) => {
        if (value === undefined) {
            setInternalValue(nextValue);
        }
        onChange?.(nextValue);
    };

    const handleOptionClick = (option: SelectItemDto) => {
        if (disabled) return;

        if (isMulti) {
            const exists = selectedItems.some((item) => item.id === option.id);
            const next = exists
                ? selectedItems.filter((item) => item.id !== option.id)
                : [...selectedItems, option];
            emitChange(next);
            return;
        }

        emitChange(option);
        setIsOpen(false);
    };

    const displayText = selectedItems.length
        ? selectedItems.map((item) => item.name).join(", ")
        : placeholder;

    return (
        <>
            <div ref={wrapperRef} className="app-search-select">
                <button
                    type="button"
                    className={`form-control d-flex justify-content-between align-items-center text-start app-search-select__trigger ${isInvalid ? "is-invalid" : ""}`}
                    disabled={disabled}
                    onClick={() => setIsOpen((prev) => !prev)}
                >
                    <span className="app-search-select__text">{displayText}</span>
                    {isOpen ? 
                        <i className="bi bi-chevron-up" style={{marginLeft: '4px'}}></i> : 
                        <i className="bi bi-chevron-down" style={{marginLeft: '4px'}}></i>
                    }
                </button>

                {isOpen && !disabled && (
                    <div className="border bg-white rounded shadow-sm mt-1 app-search-select__menu">
                        <div className="p-2">
                            <input
                                className="form-control"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Filtrar..."
                            />
                        </div>

                        <div className="app-search-select__options">
                            {filteredOptions.length === 0 ? (
                                <div className="px-3 py-2 text-muted">{noOptionsText}</div>
                            ) : (
                                filteredOptions.map((option) => {
                                    const isSelected = selectedItems.some((item) => item.id === option.id);
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            className={`w-100 text-start text-black border-0  bg-white px-3 py-2 ${!isMulti && isSelected ? "selected-option" : ""}`}
                                            onClick={() => handleOptionClick(option)}
                                        >
                                            {isMulti && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    readOnly
                                                    className="me-2"
                                                />
                                            )}
                                            {option.name}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
                {isInvalid &&
                    <div className="invalid-feedback">
                        {t("common:form.requiredFieldForm")}
                    </div>
                }
            </div>
        </>
    );
}
